import { forwardRef, useMemo, useState } from 'react'
import { format } from 'date-fns'
import { ArrowUpDown, CalendarIcon } from 'lucide-react'

import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Calendar } from '@/components/ui/calendar'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './select'
import { Label } from './label'
import { Icon } from '@/common/components/Icon'

type DateTimePickerProps = {
  label?: string
  value?: Date | null
  onChange: (value: Date | null) => void
  placeholder?: string
  disabled?: boolean
  minuteStep?: number
  className?: string
  hasError?: boolean
  mode?: 'date' | 'datetime' | 'time'
  labelClassName?: string
}

const pad2 = (value: number) => String(value).padStart(2, '0')

const getTimeParts = (value?: Date | null) => {
  if (!value) {
    return { hour: '12', minute: '00', period: 'AM' as 'AM' | 'PM' }
  }
  const hours24 = value.getHours()
  const period = hours24 >= 12 ? 'PM' : 'AM'
  const hour12 = hours24 % 12 || 12
  return { hour: pad2(hour12), minute: pad2(value.getMinutes()), period }
}

const buildDateWithTime = (
  date: Date,
  hour: string,
  minute: string,
  period: 'AM' | 'PM',
) => {
  const next = new Date(date)
  let hours = Number(hour) % 12
  if (period === 'PM') hours += 12
  next.setHours(hours, Number(minute), 0, 0)
  return next
}

export const DateTimePicker = forwardRef<
  HTMLButtonElement,
  DateTimePickerProps
>(
  (
    {
      value,
      onChange,
      placeholder = 'Pick a date',
      disabled = false,
      minuteStep = 5,
      className,
      label,
      hasError,
      mode = 'datetime',
      labelClassName,
    }: DateTimePickerProps,
    ref,
  ) => {
    const [open, setOpen] = useState(false)
    const [internalValue, setInternalValue] = useState<Date | null>(
      value ?? null,
    )
    const resolvedValue = value !== undefined ? value : internalValue
    const timeParts = useMemo(
      () => getTimeParts(resolvedValue),
      [resolvedValue],
    )

    const hours = useMemo(
      () => Array.from({ length: 12 }, (_, i) => pad2(i + 1)),
      [],
    )
    const minutes = useMemo(() => {
      const list: string[] = []
      for (let i = 0; i < 60; i += minuteStep) {
        list.push(pad2(i))
      }
      return list
    }, [minuteStep])

    const commitChange = (next: Date | null) => {
      if (value === undefined) {
        setInternalValue(next)
      }
      onChange(next)
    }

    const updateTime = (
      nextHour: string,
      nextMinute: string,
      nextPeriod: 'AM' | 'PM',
    ) => {
      const baseDate = resolvedValue ?? new Date()
      commitChange(
        buildDateWithTime(baseDate, nextHour, nextMinute, nextPeriod),
      )
    }

    return (
      <Popover open={open} onOpenChange={setOpen} modal>
        <PopoverTrigger asChild>
          <div className={cn('flex flex-col gap-2 w-full', labelClassName)}>
            {label && (
              <Label className="text-body-medium text-monochrome-1">
                {label}
              </Label>
            )}
            <Button
              ref={ref}
              type="button"
              size="sm"
              variant="tertiary"
              className={cn(
                'text-button-m text-monochrome-1 date-time-trigger !bg-monochrome-3-60 border border-monochrome-120 w-full justify-between',
                hasError && 'border border-accent-3',
                open && 'border-accent-1',
                className,
              )}
              disabled={disabled}
            >
              <span className={cn(!resolvedValue && 'text-white/60')}>
                {resolvedValue
                  ? format(
                      resolvedValue,
                      mode === 'date'
                        ? 'MMM dd, yyyy'
                        : mode === 'time'
                        ? 'hh:mm a'
                        : 'MMM dd, yyyy • hh:mm a',
                    )
                  : placeholder}
              </span>
              <Icon
                icon="arrow-up-down"
                width="8"
                height="16"
                viewBox="0 0 8 16"
              />
            </Button>
          </div>
        </PopoverTrigger>
        <PopoverContent
          className="w-[var(--radix-popover-trigger-width)/2] bg-monochrome-3-60-solid rounded-3xl p-2.5 border-monochrome-120"
          align="center"
        >
          <div
            className={cn(
              mode === 'date'
                ? 'grid-cols-1'
                : mode === 'time'
                ? 'grid-cols-1'
                : 'grid grid-cols-[400px_143px]',
              'gap-1',
            )}
          >
            {mode !== 'time' && (
              <Calendar
                mode="single"
                selected={resolvedValue ?? undefined}
                className="bg-monochrome-3-60 rounded-3xl border border-monochrome-120 w-[400px] h-[366px]"
                onSelect={(date) => {
                  if (!date) {
                    commitChange(null)
                    return
                  }
                  if (mode === 'date') {
                    // For date-only mode, set time to midnight
                    const dateOnly = new Date(date)
                    dateOnly.setHours(0, 0, 0, 0)
                    commitChange(dateOnly)
                  } else {
                    commitChange(
                      buildDateWithTime(
                        date,
                        timeParts.hour,
                        timeParts.minute,
                        timeParts.period as 'AM' | 'PM',
                      ),
                    )
                  }
                }}
              />
            )}
            {(mode === 'datetime' || mode === 'time') && (
              <div className="bg-monochrome-3-60 rounded-3xl border border-monochrome-120 p-4 w-[143px] h-[366px]">
                <div className="flex justify-between items-center">
                  <span className="text-body-medium text-white/70 flex-1">
                    Time
                  </span>
                  <Select
                    value={timeParts.period}
                    onValueChange={(value) =>
                      updateTime(
                        timeParts.hour,
                        timeParts.minute,
                        value as 'AM' | 'PM',
                      )
                    }
                  >
                    <SelectTrigger className="flex-1 bg-monochrome-3-60 rounded-full border border-monochrome-120 text-monochrome-1">
                      <SelectValue placeholder="Select Period" />
                    </SelectTrigger>
                    <SelectContent>
                      {(['AM', 'PM'] as const).map((period) => (
                        <SelectItem key={period} value={period}>
                          {period}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex gap-2 w-full mt-2">
                  <div className="max-h-[270px] overflow-y-auto flex flex-col w-full">
                    {hours.map((hour) => (
                      <button
                        key={hour}
                        type="button"
                        className={cn(
                          'text-body-medium text-monochrome-1 px-2 py-2 w-full rounded-lg',
                          timeParts.hour === hour && 'bg-accent-1 text-white',
                        )}
                        onClick={() =>
                          updateTime(
                            hour,
                            timeParts.minute,
                            timeParts.period as 'AM' | 'PM',
                          )
                        }
                      >
                        {hour}
                      </button>
                    ))}
                  </div>
                  <div className="max-h-[270px] overflow-y-auto w-full">
                    {minutes.map((minute) => (
                      <button
                        key={minute}
                        type="button"
                        className={cn(
                          'text-body-medium text-monochrome-1 px-2 py-2 w-full rounded-lg',
                          timeParts.minute === minute &&
                            'bg-accent-1 text-white',
                        )}
                        onClick={() =>
                          updateTime(
                            timeParts.hour,
                            minute,
                            timeParts.period as 'AM' | 'PM',
                          )
                        }
                      >
                        {minute}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
          <div
            className={cn(
              mode === 'time' ? 'grid-cols-2' : 'grid-cols-[400px_143px]',
              'grid gap-2 w-full mt-2',
            )}
          >
            <Button
              type="button"
              variant="secondary"
              size="sm"
              className="w-full"
              onClick={() => {
                const today = new Date()
                if (mode === 'date') {
                  // For date-only mode, set to midnight
                  today.setHours(0, 0, 0, 0)
                  commitChange(today)
                } else if (mode === 'time') {
                  // For time-only mode, set to current time
                  const now = new Date()
                  const baseDate = resolvedValue ?? new Date()
                  baseDate.setHours(now.getHours(), now.getMinutes(), 0, 0)
                  commitChange(baseDate)
                } else {
                  const base = resolvedValue ?? new Date()
                  const next = buildDateWithTime(
                    today,
                    timeParts.hour,
                    timeParts.minute,
                    timeParts.period as 'AM' | 'PM',
                  )
                  if (resolvedValue) {
                    next.setHours(base.getHours(), base.getMinutes(), 0, 0)
                  }
                  commitChange(next)
                }
              }}
            >
              {mode === 'time' ? 'Now' : 'Today'}
            </Button>
            <Button
              type="button"
              variant="primary"
              size="sm"
              className="w-full"
              onClick={() => setOpen(false)}
            >
              OK
            </Button>
          </div>
        </PopoverContent>
      </Popover>
    )
  },
)

DateTimePicker.displayName = 'DateTimePicker'

export default DateTimePicker
